<template>
  <portal to="modals" v-if="isModalVisible">
    <div class="modal-wrapper base_modal active" ref="modal" @click="close()" @keyup.esc="close()">
      <div :class="modalContainerClass" @click.stop>
        <div class="modal-content">
          <span :class="modalButtonClass" tabindex="0" @click="close" @keyup.enter="close" >
            <a v-if="!isRequestingData" class="icon"></a>
            <i>Close</i>
          </span>
          <div v-if="isRequestingData" class="loading overlay active">
            <div class="loading-spinner"></div>
          </div>
          <video-player
            v-else-if="isVideoContent"
            :close="close"
            :videoData="collectionOfElements"
            :options="options"
          />
          <material-samples
            v-else-if="isSampleMaterialContent"
            :selections="collectionOfElements"
            :close="close"
            :updateSelections="updateSelections"
          />
          <carousel
            v-else-if="isCarouselContent"
            :focus="focusedElementId"
            :close="close"
            :collection="collectionOfElements"
          />
          <form-container
            v-else-if="isFormContent"
            :form="options[0]"
            :sku="options[1]"
            :title="options[2]"
            :close="close"
          />
        </div>
      </div>
    </div>
  </portal>
</template>

<script>
import axios from 'axios';
import Carousel from './modals/Carousel';
import FormContainer from './modals/FormContainer';
import VideoPlayer from './modals/VideoPlayer';
import MaterialSamples from './modals/MaterialSamples';
import EventBus from "../event_bus";
import {
  INSPIRATION_GALLERY_LIMIT,
  INSPIRATION_MODAL_ENDPOINT,
  MATERIAL_SAMPLE_MODAL_ENDPOINT,
  VIDEO_GALLERY_MODAL_ENDPOINT,
} from '../constants';

const KEYCODE_ESCAPE = 27;
const BODY_ELEMENT = document.querySelector('body');

export default {
  components: {
    Carousel,
    FormContainer,
    VideoPlayer,
    MaterialSamples,
  },
  mounted() {
    const closeOnEscapeKey = (e) => {
      if (this.isModalVisible && e.keyCode == KEYCODE_ESCAPE) {
        this.close();
      }
    }
    document.addEventListener('keydown', closeOnEscapeKey);

    this.$once('hook:destroyed', () => {
      document.removeEventListener('keydown', closeOnEscapeKey);
    })
  },
  data() {
    return {
      isRequestingData: false,
      isVideoContent: false,
      isFormContent: false,
      isSampleMaterialContent: false,
      isCarouselContent: false,
      isModalVisible: false,
      videoData: { contentEmbedData: {} },
      focusedElementId: null,
      collectionIds: [],
      collectionOfElements: [],
      endpoint: '',
    }
  },
  created() {
    EventBus.$on('open-modal', (elementId, collectionIds, modalType, options=null) => {
      this.focusedElementId = elementId;
      this.collectionIds = collectionIds;
      this.options = options;

      if (modalType === 'video') {
        this.isVideoContent = true;
      }
      else if (modalType === 'form') {
        this.isFormContent = true;
      }
      else if (modalType === 'sample-material') {
        this.isSampleMaterialContent = true;
      }
      else if (modalType === 'carousel') {
        this.isCarouselContent = true;
      }

      this.loadElements();
    })
  },
  computed: {
    isCollectionEmpty: function() {
      return !this.collectionOfElements || this.collectionOfElements.length === 0;
    },
    modalContainerClass: function() {
      return (this.isSampleMaterialContent || this.isFormContent) ? 'config-modal-container' : 'modal-container';
    },
    modalButtonClass: function() {
      return (this.isSampleMaterialContent || this.isFormContent) ? 'sample-modal-btn-close' : 'btn-close';
    },
  },
  watch: {
    isModalVisible: {
      handler: function(value) {
        let $class = 'modal-open', $el = BODY_ELEMENT;

        if (value && !this.disablePortal) {
          $el.classList.add($class);
        } else {
          $el.classList.remove($class);
        }
      },
      immediate: true
    },
  },
  methods: {
    loadElements() {
      if (this.isSampleMaterialContent) {
        this.endpoint = MATERIAL_SAMPLE_MODAL_ENDPOINT;
      } else if (this.isCarouselContent) {
        this.endpoint = INSPIRATION_MODAL_ENDPOINT;
      } else if (this.isVideoContent) {
        this.endpoint = VIDEO_GALLERY_MODAL_ENDPOINT;
      } else if (this.isFormContent) {
        this.isModalVisible = true;
        return;
      }

      if (!this.focusedElementId && !this.collectionIds) {
        this.isModalVisible = false;
        return;
      }

      if (this.focusedElementId && !this.collectionIds.includes(this.focusedElementId)) {
        this.collectionIds.push(this.focusedElementId);
      }

      this.apiRequest(this.collectionIds);
      this.isModalVisible = true;
    },
    apiRequest: async function (ids=[])  {
      if (!ids || ids.length == 0) {
        return false;
      }

      this.isRequestingData = true;

      const requestParameters = {
        params: {
          id: ids,
          limit: INSPIRATION_GALLERY_LIMIT,
          fixedOrder: true,
        }
      }

      if (this.isCarouselContent) {
        requestParameters.params['widths'] = "1450,1386,1150,1072,788,600,524,364";
      } else if (this.isSampleMaterialContent) {
        requestParameters.params['widths'] = "592,544,486,428,364";
      }

      const {
        data: {
          data
        }
      } = await axios.get(this.endpoint, requestParameters);

      try {
        this.collectionOfElements = data.map((element) => {
          if (this.isSampleMaterialContent) {
            element['checked'] = false;
          }
          return element;
        });
      } catch (error) {
        console.log("Error", error);
      } finally {
        this.isRequestingData = false;
      }

    },
    close() {
      Object.assign(this.$data, {
        isRequestingData: false,
        focusedElementId: null,
        collectionOfElements: [],
      });
      this.isModalVisible = false;

      this.$router.push({ path: location.pathname });
    },
    updateSelections(clickedSampleId) {
      this.collectionOfElements.forEach((sample) => {
        if (sample.id === clickedSampleId) {
           sample.checked = !sample.checked;
        }
      });
    },
  },
}
</script>
